"use client";

import React, { useEffect, useState } from "react";
import { StudioTag } from "./StudioTag";
import { BookingSession, useBookingStore } from "@/lib/bookingStore";
import { isBookingClosed } from "@/lib/schedule";
import { isFeatureEnabled } from "@/lib/featureGate";
import { Clock, SunMedium } from "lucide-react";

interface ClassCardProps {
  session: BookingSession;
  compact?: boolean;
}

export const ClassCard: React.FC<ClassCardProps> = ({
  session,
  compact = false,
}) => {
  const { openCheckout, openWaitlist } = useBookingStore();
  // Compile-time isolation (plan §2.4): these are constants in the non-payment candidate,
  // so the control is never rendered as interactive and no checkout store call happens.
  const paymentsDisabled = !isFeatureEnabled("payments");
  const waitlistDisabled = !isFeatureEnabled("waitlist");

  const spotsLeft = Math.max(0, session.capacity - session.spotsBooked);
  // A manually "marked full" session closes to booking regardless of spots (item 13);
  // customers can still join the waitlist.
  // A cancelled class is never bookable. It normally does not reach this component (the public
  // catalogue filters it out), but a cached page or a direct deep link must not offer a button
  // for a class the studio cancelled.
  const isCancelled = session.isCancelled === true;
  const isSoldOut = isCancelled || spotsLeft === 0 || Boolean(session.isMarkedFull);
  const isUrgent = spotsLeft === 1 && !session.isMarkedFull;

  // Recompute the cutoff on a timer so the button closes while the page is open
  // (revision 1.1.2 item 16). The server rejects a late booking regardless.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const isClosed = isBookingClosed(
    {
      classType: session.classType,
      startsAt: session.startsAt,
      privateLeadTimeHours: session.privateLeadTimeHours,
    },
    now
  );

  const isAvailable = spotsLeft > 1 && !isClosed;

  const handleBook = () => {
    if (isClosed) return;
    if (isSoldOut) openWaitlist(session);
    else openCheckout(session);
  };

  return (
    <div className="group relative bg-white rounded-2xl p-5 border border-[#84979e]/20 transition-all duration-300 hover:border-primary/40 hover:shadow-md flex flex-col justify-between">
      <div>
        {/* Header row: Time & Studio Tag */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-1.5 text-on-surface font-semibold text-base tracking-tight">
            <Clock className="w-4 h-4 text-primary/70" />
            <span>{session.time}</span>
            <span className="text-xs text-outline font-normal">({session.durationMinutes}m)</span>
          </div>
          <StudioTag studioSlug={session.studioSlug} studioName={session.studioName} />
        </div>

        {/* Class Title & Type */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-headline font-semibold text-lg text-on-surface group-hover:text-primary transition-colors">
              {session.className}
            </h3>
            {session.isSunsetSession && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-tertiary-fixed text-on-tertiary-fixed border border-tertiary/20">
                <SunMedium className="w-3 h-3 text-tertiary" />
                Sunset Flow
              </span>
            )}
          </div>
          <span className="inline-block text-xs font-medium bg-surface-container px-2 py-0.5 rounded-md text-on-surface-variant">
            {session.classType} Reformer
          </span>
        </div>

        {/* Instructor (first name only, no photo — revision 1.1.2 item 2) */}
        <div className="mb-4">
          <span className="text-sm text-on-surface-variant font-medium">
            With <strong className="text-on-surface font-semibold">{session.instructorName}</strong>
          </span>
        </div>
      </div>

      {/* Footer: Availability & CTA */}
      <div className="pt-3 border-t border-[#84979e]/15 flex items-center justify-between gap-3">
        {/* Availability Dot & Status */}
        <div className="flex items-center gap-2">
          {isClosed && (
            <>
              <span className="h-2.5 w-2.5 rounded-full bg-outline/40 ring-2 ring-outline/10" />
              <span className="text-xs font-medium text-outline">
                Booking Closed
              </span>
            </>
          )}

          {!isClosed && isAvailable && (
            <>
              <span className="relative flex h-2.5 w-2.5">
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#506531]" />
              </span>
              <span className="text-xs font-medium text-[#506531]">
                {spotsLeft} {spotsLeft === 1 ? "spot" : "spots"} left
              </span>
            </>
          )}

          {!isClosed && isUrgent && (
            <>
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#c3a953] opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#715c09]" />
              </span>
              <span className="text-xs font-semibold text-[#715c09]">
                Only 1 spot left!
              </span>
            </>
          )}

          {!isClosed && isSoldOut && (
            <>
              <span className="h-2.5 w-2.5 rounded-full bg-outline/60" />
              <span className="text-xs font-medium text-outline">
                Fully Booked
              </span>
            </>
          )}
        </div>

        {/* Action Button (20px rounded per brand system). "Booking Closed" and
            "Fully Booked" are deliberately distinct states (item 16).

            SPB 1.1.8 non-payment staging (plan §2.4): checkout and waitlist are disabled in
            the BUILD, not hidden with CSS. The control below is a non-interactive element
            (no button, no onClick), and the routes behind it answer 503 FEATURE_DISABLED.
            Hiding the button alone would not be isolation. */}
        {paymentsDisabled || waitlistDisabled ? (
          <span
            aria-disabled="true"
            data-feature-state="booking-disabled"
            className="px-4 py-2 rounded-pill text-xs font-bold font-body bg-surface-container text-outline/80 border border-outline/20 cursor-not-allowed"
          >
            Booking belum tersedia
          </span>
        ) : (
          <button
            onClick={handleBook}
            disabled={isClosed}
            className={`px-4 py-2 rounded-pill text-xs font-bold font-body transition-all active:scale-[0.98] disabled:cursor-not-allowed ${
              isClosed
                ? "bg-surface-container text-outline/70 border border-outline/20"
                : isSoldOut
                ? "bg-surface-container text-on-surface-variant border border-outline/30 hover:bg-surface-container-high"
                : "bg-primary text-on-primary hover:bg-[#2c4e6e] shadow-sm"
            }`}
          >
            {isClosed ? "Booking Closed" : isSoldOut ? "Join Waitlist" : "Book Class"}
          </button>
        )}
      </div>
    </div>
  );
};
